import React, { useState, useContext, useEffect } from "react";
import * as Yup from "yup";
import axios from "axios";

import { useHistory } from "react-router-dom";
import { Collapsible } from "../../components";
import { OverviewForm, BusinessForm, InterestsForm, HobbiesForm } from "./index.jsx";
import { FormHelper, ValidationHelper } from "../../helpers";
import { isMerchantType } from "../../helpers/Data";
import { AjaxForm } from "../../forms/index.jsx";
import { FieldGroup } from "../../components";
import { UserContext } from "../../contexts";
import { createUserAddress } from "../../hooks/useUserAddresses";
import { isMobile } from "react-device-detect";
import { useApiError } from "../../hooks/useApiError.js";
import { Button, ConfirmDialog, Icon, Menu, useToast } from "../../components/ui";

import "./wizard-fold.scss";

const ContactWizard = ({
  className = "",
  contact: defaultContact,
  isProfile = false,
  user,
  submitLabel,
  afterSubmit,
  onCancel,
  defaultType = null,
  isRC = false,
}) => {
  const userContext = useContext(UserContext);
  const toast = useToast();
  const { showApiError } = useApiError();
  const history = useHistory();
  const [contact, setContact] = useState(defaultContact);
  const [opened, setOpened] = useState(false);
  const [type, setType] = useState(defaultType);

  // Making somebody new, as opposed to editing them or looking at a profile.
  // The same component serves all three; only this path gets the short form.
  const isCreate = !user && !isProfile;

  const fields = [
    "avatar_file_id",
    "type",
    "gender",
    "owner_id",
    "first_name",
    "last_name",
    "title",
    "company",
    "nationality_id",
    "birth_date",
    "email",
    "secondary_email",
    "phone",
    "other_phone",
    "address",
    "store_id",
    "work_role",
    "work_environment",
    "business_city_id",
    "company_url",
    "linkedin_url",
    "instagram_url",
    "interest_categories",
    "expert_categories",
    "dealer",
    "investor_type",
    "prospect_type",
    "store_url",
    "shopify_url",
    "segmentation",
    "is_sales_admin",
  ];
  const requiredFields = isRC
    ? ["first_name", "last_name", "type"]
    : ["gender", "first_name", "last_name", "type"];

  const sendUpdateUser = async (action, data = null) => {
    const newUser = await axios.put(`/api/users/${user.id}/${action}`, data);

    userContext.refresh();
    if (afterSubmit) {
      return afterSubmit(newUser.data);
    }
  };

  const updateRcUser = async ({ field, value }) => {
    const newUser = await axios.patch(`/api/rcusers/${user.id}`, {
      field,
      value,
    });

    userContext.refresh();
    if (afterSubmit) {
      return afterSubmit(newUser.data);
    }
  };

  const toggleRcAccess = async (enabled) => {
    await axios.put(`/api/users/${user.id}/toggle-rc-access`, { enabled });
    userContext.refresh();
    if (afterSubmit) {
      afterSubmit(user);
    }
  };

  // Every action is a one-click account change, so each one asks first with
  // the exact effect, then reports success or failure.
  const who =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    user?.email ||
    user?.phone ||
    "this user";

  // Claim-your-account invitations exist only for the DAO Watches stores; the
  // backend decides which stores those are (can_send_invitation).
  const [canInvite, setCanInvite] = useState(false);
  useEffect(() => {
    setCanInvite(false);
    if (isRC || !user?.id || !user.email || user.email_verified_at || !userContext.user.is_user_admin) return;

    let live = true;
    axios
      .get(`/api/users/${user.id}`)
      .then(({ data }) => live && setCanInvite(!!data.can_send_invitation))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [isRC, user?.id, user?.email, user?.email_verified_at, userContext.user.is_user_admin]);

  const inviteAction = {
    label: user?.invitation_email_sent_at ? "Resend invitation" : "Send invitation",
    title: `Email ${who} an invitation?`,
    description: `${user?.email} will get an email with a link to claim their DAO Watches account and set a password.`,
    done: `Invitation sent to ${user?.email}.`,
    run: () => sendUpdateUser("send-invitation"),
  };

  const commonActions = [
    {
      label: "Approve WL",
      title: `Approve ${who} for the waiting list?`,
      description: `${who}'s account status will be set to Approved.`,
      done: `${who} approved.`,
      run: () => updateRcUser({ field: "account_status", value: "approved" }),
    },
    {
      label: "Reject WL",
      danger: true,
      title: `Reject ${who} from the waiting list?`,
      description: `${who}'s account status will be set to Rejected.`,
      done: `${who} rejected.`,
      run: () => updateRcUser({ field: "account_status", value: "rejected" }),
    },
    {
      label: "Approve for RC Access",
      title: `Give ${who} Retail Circle access?`,
      description: `${who} will be able to sign in to Retail Circle.`,
      done: `${who} now has Retail Circle access.`,
      run: () => toggleRcAccess(true),
    },
    {
      label: "Revoke RC Access",
      danger: true,
      title: `Revoke ${who}'s Retail Circle access?`,
      description: `${who} will no longer be able to sign in to Retail Circle.`,
      done: `${who}'s Retail Circle access was revoked.`,
      run: () => toggleRcAccess(false),
    },
  ];

  const actions = isRC
    ? commonActions
    : [
        {
          label: "Make Admin",
          title: `Make ${who} an admin?`,
          description: `${who} will get admin rights in this store.`,
          done: `${who} is now an admin.`,
          run: () => sendUpdateUser("set-admin", { status: true }),
        },
        {
          label: "Remove Admin",
          danger: true,
          title: `Remove ${who}'s admin rights?`,
          description: `${who} will lose admin rights in this store.`,
          done: `${who} is no longer an admin.`,
          run: () => sendUpdateUser("set-admin", { status: false }),
        },
        {
          label: "Verify Email",
          title: `Mark ${who}'s email as verified?`,
          description: `${user?.email || "Their email"} will be marked as verified without them confirming it.`,
          done: `${who}'s email is now verified.`,
          run: () => sendUpdateUser("verify-email"),
        },
        ...(canInvite ? [inviteAction] : []),
        {
          label: "Block User",
          danger: true,
          title: `Block ${who}?`,
          description: `${who} will be blocked and can no longer use their account.`,
          done: `${who} was blocked.`,
          run: () => sendUpdateUser("block"),
        },
        ...commonActions,
      ];

  const [pendingAction, setPendingAction] = useState(null);
  const [runningAction, setRunningAction] = useState(false);

  const confirmAction = async () => {
    if (!pendingAction) return;
    setRunningAction(true);
    try {
      await pendingAction.run();
      toast.success(pendingAction.done);
      setPendingAction(null);
    } catch (err) {
      showApiError(err, pendingAction.label.toLowerCase());
    } finally {
      setRunningAction(false);
    }
  };

  const adminActionOptions = actions.map((a) => ({
    label: a.label,
    danger: a.danger,
    onSelect: () => setPendingAction(a),
  }));


  const secondButton = (
    <>
      {!!userContext.user.is_user_admin && !!user && (
        <div className="me-3">
          {/* The shared Menu (portalled, flips up, sits above the user drawer).
              The legacy DdButton rendered its options inline under the drawer
              footer — off-screen and unclickable. */}
          <Menu
            label="User actions"
            align="start"
            items={adminActionOptions}
            trigger={
              <Button
                variant="secondary"
                trailingIcon={<Icon name="ChevronDown" size="sm" />}
                data-btn="user-actions-open"
              >
                Choose actions
              </Button>
            }
          />
          <ConfirmDialog
            open={!!pendingAction}
            onClose={() => setPendingAction(null)}
            onConfirm={confirmAction}
            loading={runningAction}
            danger={!!pendingAction?.danger}
            title={pendingAction?.title}
            description={pendingAction?.description}
            confirmLabel={pendingAction?.label}
          />
        </div>
      )}
    </>
  );

  // Extra address sets typed while CREATING a user. There is no user id to
  // attach them to until the record exists, so they are held here and written
  // immediately after creation. The first address still travels with the user
  // payload itself (it becomes users.address_id, the default).
  const [extraAddresses, setExtraAddresses] = useState([]);

  const afterSubmitted = async (newContact) => {
    const created = newContact?.user;

    // Best-effort, and deliberately AFTER the user exists rather than as part of
    // the same request: the user is the thing that must not be lost. Each
    // address is posted individually so one bad row cannot take the others with
    // it, and a failure is reported rather than swallowed — the addresses are
    // still on screen to retry from.
    if (created?.id && extraAddresses.length) {
      const failed = [];
      for (const a of extraAddresses) {
        if (!a?.country_id) continue; // country is NOT NULL; nothing to save yet
        try {
          await createUserAddress(created.id, { ...a, country_id: Number(a.country_id) });
        } catch (e) {
          failed.push(a);
        }
      }
      setExtraAddresses(failed);
      if (failed.length) {
        console.error(`${failed.length} extra address(es) could not be saved`);
      }
    }

    userContext.refresh();
    if (afterSubmit) {
      return afterSubmit(created);
    }
  };

  const formProps = {
    method: user ? "put" : "post",
    url: isRC
      ? `/api/rcusers/${user?.id}`
      : user
      ? `/api/users/${user.id}`
      : "/api/users/pre-register",
    fields,
    validationSchema: ValidationHelper.schema(requiredFields, fields, {
      email: Yup.string()
        .nullable()
        .email()
        .test(
          "email",
          "Either Email or Phone is mandatory",
          async (email, ctx) => {
            const { phone } = ctx.parent;

            return !!(email || phone);
          }
        ),
      phone: Yup.string()
        .nullable()
        .test(
          "phone",
          "Either Phone or Email is mandatory",
          async (phone, ctx) => {
            const { email } = ctx.parent;

            return !!(phone || email);
          }
        ),
      owner_id: Yup.string()
        .nullable()
        .test("owner_id", "Owner Id is required", async (owner_id, ctx) => {
          const { is_sales_admin } = ctx.parent;
          if (isRC) return true;
          return !is_sales_admin ? !!owner_id : true;
        }),
      store_id: Yup.string()
        .nullable()
        .test("store_id", "Store is mandatory", async (store_id, ctx) => {
          const { type } = ctx.parent;

          // Every MERCHANT-grouped type, not just "merchant": the backend's
          // UserValidationService::getBusinessRules() has always required
          // store_id for supplier and personal_shopper too, so gating only on
          // "merchant" turned a missing store into an opaque 422 from the
          // server instead of an inline error on the field. Watchmaker joins
          // the same group, and NEW WATCHMAKER has to fail readably.
          return !!(store_id || !isMerchantType(type));
        }),
    }),
    initialValues: FormHelper.initialValues(fields, user),
    requiredFields: [],
    submitLabel,
    afterSubmit: afterSubmitted,
    // Fontaine M56B: the self-profile persists every edit automatically —
    // no Save button on /profile (RC rule). WHAT is persisted is untouched:
    // same PUT /api/users/{id}, same full `fields` payload the button sent.
    // The admin "Choose Actions" dropdown (secondButton) stays — those are
    // transactional commands (approve/block/verify email), not field saves.
    // Create and admin-edit wizards keep their submit button: creating a
    // user IS a transaction.
    autoSave: isProfile && !!user,
    showErrorNextToSubmit: true,
    onCancel,
    secondButton,
    // Always render the submit/cancel footer. The previous gating on
    // `opened` (Business Collapsible expanded) hid the action buttons
    // when admin filled out only the Overview section, leaving them
    // unable to submit a new client from V2's ASSIGN CLIENT flow.
    showFooter: true,
    submitContainerClassName:
      !user && !isProfile
        ? "users-create-actions"
        : isMobile
          ? "me-3"
          : "mx-auto d-flex justify-content-end",
  };

  return (
    <AjaxForm {...formProps}>
      <div className={`collapsibles ${className}`}>
        {isCreate ? (
          /**
           * CREATING: four fields and a fold.
           *
           * Adding a contact mid-flow — ASSIGN CLIENT on a request, a supplier
           * on a listing — used to open Overview AND Business at once: 29
           * fields for somebody who had a name and a number. Only first_name,
           * last_name and type are required (plus gender outside RC), so those
           * stay on screen and the whole record waits behind one fold.
           *
           * It is a fold, not a second step: everything is still one form and
           * one submit, so nothing is lost by opening it late (Alex,
           * 2026-10-27).
           */
          <>
            <OverviewForm
              part="short"
              setType={setType}
              isProfile={isProfile}
              defaultType={defaultType}
              user={user}
              isRC={isRC}
              showAvatar
              extraAddresses={extraAddresses}
              setExtraAddresses={setExtraAddresses}
            />

            {/* A native <details>, NOT <Collapsible>. That component returns
                its children bare inside the request flows (V2ModeContext),
                so a fold built on it has no trigger and never closes — which
                is how every field ended up on screen in ASSIGN CLIENT, the
                one place this short form exists for. */}
            <details
              className="rc-wizfold"
              onToggle={(e) => setOpened(e.currentTarget.open)}
            >
              <summary>
                <svg
                  className="rc-wizfold__chevron"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M9 6l6 6-6 6" />
                </svg>
                More details
                <span className="rc-wizfold__hint">everything else is optional</span>
              </summary>
              <div className="rc-wizfold__body">
                <OverviewForm
                  part="rest"
                  setType={setType}
                  isProfile={isProfile}
                  defaultType={defaultType}
                  user={user}
                  isRC={isRC}
                  showAvatar
                  extraAddresses={extraAddresses}
                  setExtraAddresses={setExtraAddresses}
                />
                <BusinessForm
                  type={type || "client"}
                  user={user}
                  isProfile={isProfile}
                  isRC={isRC}
                />
              </div>
            </details>
          </>
        ) : (
          <>
            <Collapsible trigger="Overview" open={true}>
              <OverviewForm
                setType={setType}
                isProfile={isProfile}
                defaultType={defaultType}
                user={user}
                isRC={isRC}
                showAvatar={false}
                extraAddresses={extraAddresses}
                setExtraAddresses={setExtraAddresses}
              />
            </Collapsible>

            <Collapsible
              trigger="Business"
              open={false}
              triggerDisabled={false}
              onOpening={() => setOpened(true)}
              onClosing={() => setOpened(false)}
            >
              <BusinessForm
                type={type || "client"}
                user={user}
                isProfile={isProfile}
                isRC={isRC}
              />
            </Collapsible>
          </>
        )}
      </div>
    </AjaxForm>
  );
};

export default ContactWizard;
